import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search, X, FileText, Truck, Package, AlertTriangle,
  ChevronRight, Loader2, Receipt, DollarSign,
  CheckCircle2, Clock, AlertCircle, Copy,
  TrendingUp, TrendingDown, ShoppingCart, BarChart3, Percent,
  ArrowUp, ArrowDown, ArrowUpDown, Pencil, Trash2, Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderCostTab() {
  const navigate = useNavigate();
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Order detail state
  const [orderData, setOrderData] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [selectedRef, setSelectedRef] = useState("");

  // Orders overview state
  const [overviewOrders, setOverviewOrders] = useState([]);
  const [overviewCounts, setOverviewCounts] = useState({ missing_transport: 0, missing_sales: 0, missing_purchase: 0, complete: 0, duplicates: 0 });
  const [overviewTotal, setOverviewTotal] = useState(0);
  const [overviewSummary, setOverviewSummary] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [overviewPeriod, setOverviewPeriod] = useState("all");
  const [statusFilter, setStatusFilter] = useState("missing_transport");
  const [sortKey, setSortKey] = useState("last_activity");
  const [sortDir, setSortDir] = useState("desc");

  const searchTimeout = useRef(null);

  // Load overview on mount and when period/filter changes
  useEffect(() => {
    fetchOverview();
  }, [overviewPeriod, statusFilter]);

  const fetchOverview = async () => {
    setLoadingOverview(true);
    try {
      const res = await axios.get(`${API}/history/orders-overview?period=${overviewPeriod}&status_filter=${statusFilter}&limit=100`);
      setOverviewOrders(res.data.orders || []);
      setOverviewCounts(res.data.counts || { all: 0, complete: 0, missing_transport: 0, errors: 0 });
      setOverviewTotal(res.data.total || 0);
      setOverviewSummary(res.data.summary || null);
    } catch {
      setOverviewOrders([]);
    } finally {
      setLoadingOverview(false);
    }
  };

  const searchReferences = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/history/order-search?q=${encodeURIComponent(q)}&limit=15`);
      setSearchResults(res.data.references || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchReferences(val), 250);
  };

  const selectReference = async (ref) => {
    setSelectedRef(ref);
    setSearchQuery(ref);
    setSearchResults([]);
    setLoadingOrder(true);
    try {
      const res = await axios.get(`${API}/history/order-cost/${encodeURIComponent(ref)}`);
      setOrderData(res.data);
    } catch {
      setOrderData(null);
    } finally {
      setLoadingOrder(false);
    }
  };

  const clearOrderDetail = () => {
    setSearchQuery("");
    setSearchResults([]);
    setOrderData(null);
    setSelectedRef("");
  };

  const formatDate = (d) => {
    if (!d) return "\u2014";
    return new Date(d).toLocaleDateString("sv-SE", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const formatKr = (v) => {
    if (v === null || v === undefined) return "\u2014";
    return `${Math.round(v).toLocaleString("sv-SE")} kr`;
  };

  const statusConfig = {
    complete: { label: "Komplett", icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", border: "border-green-500/30" },
    missing_transport: { label: "Saknar frakt", icon: Truck, color: "text-yellow-500", bg: "bg-yellow-500/10", border: "border-yellow-500/30" },
    missing_sales: { label: "Saknar saljpris", icon: DollarSign, color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/30" },
    missing_purchase: { label: "Saknar inkopspris", icon: AlertCircle, color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/30" },
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "last_activity" ? "desc" : "desc");
    }
  };

  const sortedOrders = useMemo(() => {
    if (!overviewOrders.length) return overviewOrders;
    const sorted = [...overviewOrders].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      // Handle nulls — push to bottom
      if (av === null || av === undefined) av = sortDir === "asc" ? Infinity : -Infinity;
      if (bv === null || bv === undefined) bv = sortDir === "asc" ? Infinity : -Infinity;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return sorted;
  }, [overviewOrders, sortKey, sortDir]);

  const trends = overviewSummary?.trends;
  const [marginExpanded, setMarginExpanded] = useState(false);

  return (
    <div className="space-y-6">
      {/* Summary Badges */}
      {overviewSummary && !orderData && !loadingOrder && (
        <div data-testid="summary-badges">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <SummaryBadge
              label="Ordrar (kompletta)"
              value={overviewSummary.total_orders?.toLocaleString("sv-SE")}
              sub={`${overviewSummary.total_quantity?.toLocaleString("sv-SE")} produkter`}
              icon={ShoppingCart}
              color="text-blue-400"
              bg="bg-blue-500/10"
              trend={trends?.orders}
              testId="badge-orders"
            />
            <SummaryBadge
              label="Omsattning"
              value={formatKr(overviewSummary.total_revenue)}
              sub={overviewSummary.orders_with_profit > 0 ? `Snitt ${formatKr(overviewSummary.avg_order_value)}/order` : null}
              icon={Receipt}
              color="text-blue-500"
              bg="bg-blue-500/10"
              trend={trends?.revenue}
              testId="badge-revenue"
            />
            <SummaryBadge
              label="Vinst"
              value={formatKr(overviewSummary.total_profit)}
              sub={overviewSummary.orders_with_profit > 0 ? `${overviewSummary.orders_with_profit} ordrar` : null}
              icon={overviewSummary.total_profit >= 0 ? TrendingUp : TrendingDown}
              color={overviewSummary.total_profit >= 0 ? "text-green-500" : "text-red-500"}
              bg={overviewSummary.total_profit >= 0 ? "bg-green-500/10" : "bg-red-500/10"}
              trend={trends?.profit}
              testId="badge-profit"
            />
            <Card
              className="overflow-hidden cursor-pointer transition-colors hover:border-primary/30"
              onClick={() => setMarginExpanded(!marginExpanded)}
              data-testid="badge-margin"
            >
              <CardContent className="p-3">
                <div className="flex items-start gap-2.5">
                  <div className={`p-2 rounded-lg ${overviewSummary.avg_margin !== null && overviewSummary.avg_margin >= 0 ? "bg-green-500/10" : "bg-red-500/10"} shrink-0`}>
                    <Percent className={`h-4 w-4 ${overviewSummary.avg_margin !== null && overviewSummary.avg_margin >= 0 ? "text-green-500" : "text-red-500"}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                      Marginal
                      <ChevronRight className={`h-3 w-3 transition-transform ${marginExpanded ? "rotate-90" : ""}`} />
                    </p>
                    <p className={`text-lg font-bold leading-tight ${overviewSummary.avg_margin !== null && overviewSummary.avg_margin >= 0 ? "text-green-500" : "text-red-500"}`}>
                      {overviewSummary.avg_margin !== null ? `${overviewSummary.avg_margin}%` : "\u2014"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {overviewSummary.orders_with_profit > 0 ? `${overviewSummary.orders_with_profit} kompletta` : null}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <SummaryBadge
              label="Marginal utan transport"
              value={overviewSummary.margin_excl_transport !== null ? `${overviewSummary.margin_excl_transport}%` : "\u2014"}
              sub={overviewSummary.profit_excl_transport !== null ? `Vinst ${formatKr(overviewSummary.profit_excl_transport)}` : null}
              icon={BarChart3}
              color={overviewSummary.margin_excl_transport !== null && overviewSummary.margin_excl_transport >= 0 ? "text-green-500" : "text-red-500"}
              bg={overviewSummary.margin_excl_transport !== null && overviewSummary.margin_excl_transport >= 0 ? "bg-green-500/10" : "bg-red-500/10"}
              testId="badge-margin-excl"
            />
          </div>

          {/* Expandable carrier details under Marginal */}
          {marginExpanded && (overviewSummary.carriers?.bring || overviewSummary.carriers?.dsv) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2 ml-0 lg:ml-[60%] lg:grid-cols-2" data-testid="carrier-details">
              {overviewSummary.carriers?.bring && (
                <Card className="border-green-500/20">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Truck className="h-3.5 w-3.5 text-green-500" />
                      <span className="text-xs font-medium text-green-500">Bring</span>
                    </div>
                    <p className="text-sm font-bold">{formatKr(overviewSummary.carriers.bring.transport_cost)}</p>
                    <p className="text-xs text-muted-foreground">
                      Marginal {overviewSummary.carriers.bring.margin ?? "—"}% ({overviewSummary.carriers.bring.order_count} ordrar)
                    </p>
                  </CardContent>
                </Card>
              )}
              {overviewSummary.carriers?.dsv && (
                <Card className="border-blue-500/20">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Truck className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-xs font-medium text-blue-500">DSV</span>
                    </div>
                    <p className="text-sm font-bold">{formatKr(overviewSummary.carriers.dsv.transport_cost)}</p>
                    <p className="text-xs text-muted-foreground">
                      Marginal {overviewSummary.carriers.dsv.margin ?? "—"}% ({overviewSummary.carriers.dsv.order_count} ordrar)
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search + Upload row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Sok referensnummer / ordernummer..."
            className="pl-10 pr-10"
            data-testid="order-cost-search"
          />
          {searchQuery && (
            <button
              onClick={clearOrderDetail}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {searchResults.length > 0 && (
            <div className="absolute z-50 top-full mt-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {searchResults.map((r) => (
                <button
                  key={r.reference}
                  onClick={() => selectReference(r.reference)}
                  className="w-full text-left px-4 py-2.5 hover:bg-muted/50 flex items-center justify-between border-b border-border/50 last:border-0"
                  data-testid={`ref-result-${r.reference}`}
                >
                  <div>
                    <span className="font-mono font-medium">{r.reference}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {r.product_count} produkt{r.product_count !== 1 ? "er" : ""}, {r.total_quantity} st
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.has_transport_cost && (
                      <Badge variant="outline" className="text-green-500 border-green-500/30 text-xs">
                        <Truck className="h-3 w-3 mr-1" /> Frakt
                      </Badge>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          )}
          {searching && (
            <div className="absolute z-50 top-full mt-1 w-full bg-popover border border-border rounded-lg shadow-lg p-4 text-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Soker...
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/invoice-scan")} variant="outline" className="whitespace-nowrap" data-testid="go-to-invoice-scan">
            <FileText className="h-4 w-4 mr-2" /> Transportfakturor
          </Button>
        </div>
      </div>

      {/* Order Detail View */}
      {loadingOrder && (
        <div className="text-center py-12 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          Hamtar orderkostnad...
        </div>
      )}

      {orderData && !loadingOrder && (
        <OrderDetailView
          orderData={orderData}
          selectedRef={selectedRef}
          formatKr={formatKr}
          formatDate={formatDate}
          onClose={clearOrderDetail}
          onRefresh={() => selectReference(selectedRef)}
        />
      )}

      {/* Orders Overview - only show when no order detail */}
      {!orderData && !loadingOrder && (
        <div className="space-y-4">
          {/* Period + Status filter */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {["all", "quarter", "month", "week", "today"].map((p) => (
                <Button
                  key={p}
                  variant={overviewPeriod === p ? "default" : "outline"}
                  size="sm"
                  onClick={() => setOverviewPeriod(p)}
                  data-testid={`overview-period-${p}`}
                >
                  {{ all: "Alla", quarter: "90 dagar", month: "30 dagar", week: "7 dagar", today: "Idag" }[p]}
                </Button>
              ))}
            </div>
          </div>

          {/* Status filter cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { key: "missing_transport", label: "Saknar frakt", icon: Truck, count: overviewCounts.missing_transport, color: "text-yellow-500", bg: "bg-yellow-500/10" },
              { key: "missing_sales", label: "Saknar saljpris", icon: DollarSign, count: overviewCounts.missing_sales, color: "text-orange-500", bg: "bg-orange-500/10" },
              { key: "missing_purchase", label: "Saknar inkopspris", icon: AlertCircle, count: overviewCounts.missing_purchase, color: "text-red-500", bg: "bg-red-500/10" },
              { key: "duplicates", label: "Dubletter", icon: Copy, count: overviewCounts.duplicates || 0, color: "text-purple-500", bg: "bg-purple-500/10" },
              { key: "complete", label: "Kompletta", icon: CheckCircle2, count: overviewCounts.complete, color: "text-green-500", bg: "bg-green-500/10" },
            ].map(({ key, label, icon: Icon, count, color, bg }) => (
              <Card
                key={key}
                className={`cursor-pointer transition-all ${statusFilter === key ? "ring-2 ring-primary" : "hover:bg-muted/30"}`}
                onClick={() => setStatusFilter(key)}
                data-testid={`filter-${key}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg ${bg}`}>
                      <Icon className={`h-4 w-4 ${color}`} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="text-xl font-bold">{count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Orders table */}
          {loadingOverview ? (
            <div className="text-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Laddar ordrar...
            </div>
          ) : overviewOrders.length === 0 ? (
            <Card className="p-8 text-center">
              <Package className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground">Inga ordrar hittades for vald period och filter</p>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Referens</TableHead>
                    <SortableHead label="Produkter" sortKey="total_quantity" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="text-right hidden sm:table-cell" />
                    <SortableHead label="Varukostnad" sortKey="total_product_cost" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="text-right hidden md:table-cell" />
                    <SortableHead label="Transport" sortKey="transport_cost" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="text-right hidden md:table-cell" />
                    <SortableHead label="Intakt" sortKey="total_revenue" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="text-right hidden sm:table-cell" />
                    <SortableHead label="Resultat" sortKey="estimated_profit" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="text-right" />
                    <SortableHead label="Datum" sortKey="last_activity" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="hidden lg:table-cell" />
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOrders.map((order) => {
                    const sc = statusConfig[order.status] || statusConfig.errors;
                    const StatusIcon = sc.icon;
                    return (
                      <TableRow
                        key={order.reference}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => selectReference(order.reference)}
                        data-testid={`order-row-${order.reference}`}
                      >
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <div className={`p-1 rounded ${sc.bg}`}>
                                  <StatusIcon className={`h-4 w-4 ${sc.color}`} />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-medium">{sc.label}</p>
                                {order.warnings?.map((r, i) => (
                                  <p key={i} className="text-xs text-muted-foreground">{r}</p>
                                ))}
                                {order.status === "missing_transport" && <p className="text-xs text-yellow-400">Transportfaktura ej skannats</p>}
                                {order.status === "missing_sales" && <p className="text-xs text-orange-400">Saknar forsaljningspris</p>}
                                {order.status === "missing_purchase" && <p className="text-xs text-red-400">Saknar inkopspris</p>}
                                {order.has_duplicate && <p className="text-xs text-purple-400">Finns pa flera fakturor!</p>}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-medium">{order.reference}</span>
                          {order.carrier && (
                            <Badge variant="outline" className="ml-2 text-xs uppercase">{order.carrier}</Badge>
                          )}
                          {order.has_duplicate && (
                            <Badge variant="outline" className="ml-1 text-xs text-purple-500 border-purple-500/30">
                              <Copy className="h-3 w-3 mr-1" /> Dubblett
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right hidden sm:table-cell">
                          {order.product_count} <span className="text-muted-foreground text-xs">({order.total_quantity} st)</span>
                        </TableCell>
                        <TableCell className="text-right hidden md:table-cell">
                          {order.total_product_cost ? (
                            <span className="text-orange-500">{formatKr(order.total_product_cost)}</span>
                          ) : <span className="text-muted-foreground text-xs">—</span>}
                        </TableCell>
                        <TableCell className="text-right hidden md:table-cell">
                          {order.has_transport ? (
                            <span className="text-green-500">{formatKr(order.transport_cost)}</span>
                          ) : (
                            <span className="text-yellow-500 text-xs flex items-center justify-end gap-1"><Clock className="h-3 w-3" /> Ej reg.</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right hidden sm:table-cell">
                          {order.total_revenue ? (
                            <span className="text-blue-500">{formatKr(order.total_revenue)}</span>
                          ) : "\u2014"}
                        </TableCell>
                        <TableCell className="text-right">
                          {order.estimated_profit !== null && order.estimated_profit !== undefined ? (
                            <span className={`font-bold ${order.estimated_profit >= 0 ? "text-green-500" : "text-red-500"}`}>
                              {order.estimated_profit >= 0 ? "+" : ""}{formatKr(order.estimated_profit)}
                            </span>
                          ) : "\u2014"}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                          {formatDate(order.last_activity)}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function OrderDetailView({ orderData, selectedRef, formatKr, formatDate, onClose, onRefresh }) {
  const [editingEan, setEditingEan] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editingQtyEan, setEditingQtyEan] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [editingTransportId, setEditingTransportId] = useState(null);
  const [editTransport, setEditTransport] = useState("");
  const [editingCustomerFreight, setEditingCustomerFreight] = useState(false);
  const [editCustFreight, setEditCustFreight] = useState("");
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [addEan, setAddEan] = useState("");
  const [addQty, setAddQty] = useState("1");
  const [saving, setSaving] = useState(false);

  const savePrice = async (ean) => {
    const val = parseFloat(editPrice.replace(",", "."));
    if (!val || val <= 0) { toast.error("Ange ett giltigt pris"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/history/update-sales-price`, { ean, order_number: selectedRef, price_incl_vat: val });
      toast.success("Forsaljningspris uppdaterat");
      setEditingEan(null);
      setEditPrice("");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte spara pris"); }
    finally { setSaving(false); }
  };

  const saveQuantity = async (ean) => {
    const val = parseInt(editQty);
    if (isNaN(val) || val < 1) { toast.error("Ange ett giltigt antal (minst 1)"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/history/update-order-quantity`, { ean, reference: selectedRef, new_quantity: val });
      toast.success(`Antal andrat till ${val}`);
      setEditingQtyEan(null);
      setEditQty("");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte andra antal"); }
    finally { setSaving(false); }
  };

  const removeProduct = async (ean, artikelnummer) => {
    if (!confirm(`Ta bort ${artikelnummer || ean} fran order ${selectedRef}?`)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/history/remove-order-product`, { ean, reference: selectedRef });
      toast.success("Produkt borttagen");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte ta bort produkt"); }
    finally { setSaving(false); }
  };

  const addProduct = async () => {
    const ean = addEan.trim();
    const qty = parseInt(addQty);
    if (!ean) { toast.error("Ange EAN / streckkod"); return; }
    if (isNaN(qty) || qty < 1) { toast.error("Ange giltigt antal"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/history/add-order-product`, { ean, reference: selectedRef, quantity: qty });
      const d = res.data;
      const name = d.artikelnummer || d.ean;
      if (d.price_info?.unit_cost) {
        toast.success(`${name} tillagd (${Math.round(d.price_info.unit_cost)} kr/st) x${qty}`);
      } else {
        toast.warning(`${name} tillagd x${qty} — saknar inkopspris`);
      }
      setAddEan("");
      setAddQty("1");
      setShowAddProduct(false);
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte lagga till produkt"); }
    finally { setSaving(false); }
  };

  const saveTransportCost = async (tcId) => {
    const val = parseFloat(editTransport.replace(",", "."));
    if (isNaN(val) || val < 0) { toast.error("Ange ett giltigt belopp"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/history/update-transport-cost`, { reference: selectedRef, transport_cost_id: tcId || "", new_cost: val });
      toast.success("Transportkostnad uppdaterad");
      setEditingTransportId(null);
      setEditTransport("");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte andra transportkostnad"); }
    finally { setSaving(false); }
  };

  const saveCustomerFreight = async () => {
    const val = parseFloat(editCustFreight.replace(",", "."));
    if (isNaN(val) || val < 0) { toast.error("Ange ett giltigt belopp"); return; }
    const exclVat = val / 1.25;
    setSaving(true);
    try {
      await axios.post(`${API}/history/update-customer-freight`, { reference: selectedRef, freight_excl_vat: exclVat });
      toast.success("Kundfrakt uppdaterad");
      setEditingCustomerFreight(false);
      setEditCustFreight("");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte andra kundfrakt"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="order-cost-result">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
          <Receipt className="h-5 w-5 text-primary" />
          Order {selectedRef}
        </h3>
        <div className="flex gap-2 items-center">
          {orderData.bookings_count > 0 && (
            <Badge variant="outline" className="text-blue-500 border-blue-500/30">
              {orderData.bookings_count} bokning{orderData.bookings_count !== 1 ? "ar" : ""}
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Varukostnad</p>
            <p className="text-xl font-bold text-orange-500">{formatKr(orderData.totals.total_product_cost)}</p>
            {(orderData.totals.total_chemical_tax > 0 || orderData.totals.total_freight_cost > 0) && (
              <p className="text-xs text-muted-foreground">
                Inkop {formatKr(orderData.totals.total_purchase_price)} + kem.skatt {formatKr(orderData.totals.total_chemical_tax)} + frakt {formatKr(orderData.totals.total_freight_cost)}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Transportkostnad (netto)</p>
            <p className="text-xl font-bold text-red-500">
              {orderData.totals.total_transport_cost > 0
                ? formatKr(orderData.totals.total_transport_cost)
                : <span className="text-muted-foreground flex items-center gap-1 text-base"><Clock className="h-4 w-4" /> Ej registrerad</span>}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Kundfrakt (inkl moms)</p>
            {editingCustomerFreight ? (
              <div className="flex items-center gap-1 mt-1">
                <Input
                  type="text"
                  value={editCustFreight}
                  onChange={(e) => setEditCustFreight(e.target.value)}
                  placeholder="Belopp inkl moms"
                  className="w-24 h-7 text-right text-sm"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") saveCustomerFreight(); if (e.key === "Escape") setEditingCustomerFreight(false); }}
                  data-testid="edit-customer-freight-input"
                />
                <Button size="icon" variant="ghost" className="h-7 w-7 text-green-500" onClick={saveCustomerFreight} disabled={saving}>
                  <CheckCircle2 className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingCustomerFreight(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <button
                className="text-xl font-bold text-blue-400 inline-flex items-center gap-1 hover:text-primary transition-colors"
                onClick={() => { setEditingCustomerFreight(true); setEditCustFreight(orderData.customer_freight ? String(Math.round(orderData.customer_freight.incl_vat)) : ""); }}
                data-testid="edit-customer-freight"
              >
                {orderData.customer_freight ? formatKr(orderData.customer_freight.incl_vat) : "\u2014"}
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
          </CardContent>
        </Card>
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Forsaljning (inkl moms)</p><p className="text-xl font-bold text-blue-500">{orderData.totals.total_sales_incl_vat ? formatKr(orderData.totals.total_sales_incl_vat) : "\u2014"}</p></CardContent></Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Resultat</p>
            <p className={`text-xl font-bold ${orderData.totals.total_profit !== null ? orderData.totals.total_profit >= 0 ? "text-green-500" : "text-red-500" : "text-muted-foreground"}`}>
              {orderData.totals.total_profit !== null ? `${orderData.totals.total_profit >= 0 ? "+" : ""}${formatKr(orderData.totals.total_profit)}` : "\u2014"}
            </p>
          </CardContent>
        </Card>
      </div>

      {orderData.duplicate_warning && (
        <Card className="border-purple-500/30 bg-purple-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg mt-0.5">
              <Copy className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="font-medium text-purple-400 text-sm">Dubblettvarning</p>
              <p className="text-sm text-muted-foreground">{orderData.duplicate_warning.message}</p>
              <div className="mt-1 space-y-0.5">
                {orderData.duplicate_warning.invoices?.map((inv, i) => (
                  <p key={i} className="text-xs font-mono">{inv}</p>
                ))}
              </div>
              <p className="text-sm font-bold mt-1">Totalt debiterat: {formatKr(orderData.duplicate_warning.total_charged)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {orderData.transport_costs?.length > 0 && (
        <Card className="border-green-500/20 bg-green-500/5">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Truck className="h-4 w-4 text-green-500" />Registrerad transportkostnad</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {orderData.transport_costs.map((tc, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs uppercase">{tc.carrier}</Badge>
                  <span className="text-sm">{tc.recipient}</span>
                  <span className="text-xs text-muted-foreground">Faktura {tc.invoice_number}</span>
                </div>
                {editingTransportId === (tc.id || `tc-${i}`) ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="text"
                      value={editTransport}
                      onChange={(e) => setEditTransport(e.target.value)}
                      placeholder="Belopp"
                      className="w-24 h-7 text-right text-sm"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter") saveTransportCost(tc.id); if (e.key === "Escape") setEditingTransportId(null); }}
                      data-testid={`edit-transport-input-${i}`}
                    />
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-green-500" onClick={() => saveTransportCost(tc.id)} disabled={saving}>
                      <CheckCircle2 className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingTransportId(null)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <button
                    className="font-bold inline-flex items-center gap-1 hover:text-primary transition-colors"
                    onClick={() => { setEditingTransportId(tc.id || `tc-${i}`); setEditTransport(String(Math.round(tc.total_cost))); }}
                    data-testid={`edit-transport-${i}`}
                  >
                    {formatKr(tc.total_cost)}
                    <Pencil className="h-3 w-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {orderData.products.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artikel</TableHead>
                <TableHead className="text-right">Antal</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Inkopspris/st</TableHead>
                <TableHead className="text-right hidden md:table-cell">Kostnad/st</TableHead>
                <TableHead className="text-right">Radkostnad</TableHead>
                <TableHead className="text-right">Forsaljning (inkl moms)</TableHead>
                <TableHead className="text-right">Vinst</TableHead>
                <TableHead className="text-right">Marginal</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orderData.products.map((p, i) => (
                <TableRow key={p.ean || i} data-testid={`order-product-${p.ean}`}>
                  <TableCell>
                    <div>
                      <p className="font-mono font-medium">{p.artikelnummer || p.ean}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[180px]">{p.ean}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {editingQtyEan === p.ean ? (
                      <div className="flex items-center gap-1 justify-end">
                        <Input
                          type="number"
                          min="1"
                          value={editQty}
                          onChange={(e) => setEditQty(e.target.value)}
                          className="w-16 h-7 text-right text-sm"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === "Enter") saveQuantity(p.ean); if (e.key === "Escape") setEditingQtyEan(null); }}
                          data-testid={`edit-qty-input-${p.ean}`}
                        />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-green-500" onClick={() => saveQuantity(p.ean)} disabled={saving}>
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        className="font-medium inline-flex items-center gap-1 hover:text-primary transition-colors"
                        onClick={() => { setEditingQtyEan(p.ean); setEditQty(String(p.quantity)); }}
                        data-testid={`edit-qty-${p.ean}`}
                      >
                        {p.quantity} st
                        <Pencil className="h-3 w-3 text-muted-foreground" />
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-right hidden sm:table-cell">
                    {p.purchase_price_sek ? formatKr(p.purchase_price_sek) : <span className="text-muted-foreground text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-right hidden md:table-cell">
                    {p.unit_cost ? (
                      <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help">{formatKr(p.unit_cost)}</TooltipTrigger>
                        <TooltipContent><p>Inkop: {formatKr(p.purchase_price_sek)}</p><p>Frakt: {formatKr(p.freight_cost_product)}</p><p>Kem.skatt: {formatKr(p.chemical_tax)}</p></TooltipContent>
                      </Tooltip></TooltipProvider>
                    ) : "\u2014"}
                  </TableCell>
                  <TableCell className="text-right font-medium text-orange-500">{p.line_cost ? formatKr(p.line_cost) : "\u2014"}</TableCell>
                  <TableCell className="text-right">
                    {editingEan === p.ean ? (
                      <div className="flex items-center gap-1 justify-end">
                        <Input
                          type="text"
                          value={editPrice}
                          onChange={(e) => setEditPrice(e.target.value)}
                          placeholder="Pris inkl moms"
                          className="w-24 h-7 text-right text-sm"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === "Enter") savePrice(p.ean); if (e.key === "Escape") setEditingEan(null); }}
                          data-testid={`edit-price-input-${p.ean}`}
                        />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-green-500" onClick={() => savePrice(p.ean)} disabled={saving}>
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingEan(null)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <span
                        className={`cursor-pointer hover:underline ${p.sales_price_incl_vat ? "text-blue-500 font-medium" : "text-orange-400 text-xs italic"}`}
                        onClick={() => { setEditingEan(p.ean); setEditPrice(p.sales_price_incl_vat ? String(Math.round(p.sales_price_incl_vat)) : ""); }}
                        data-testid={`edit-price-${p.ean}`}
                      >
                        {p.sales_price_incl_vat ? formatKr(p.sales_price_incl_vat) : "Lagg till pris"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {p.gross_profit !== null ? (
                      <span className={p.gross_profit >= 0 ? "text-green-500 font-bold" : "text-red-500 font-bold"}>
                        {p.gross_profit >= 0 ? "+" : ""}{formatKr(p.gross_profit)}
                      </span>
                    ) : "\u2014"}
                  </TableCell>
                  <TableCell className="text-right">
                    {p.margin !== null ? (
                      <Badge variant={p.margin >= 20 ? "success" : p.margin >= 0 ? "warning" : "destructive"}>{p.margin}%</Badge>
                    ) : "\u2014"}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-red-500"
                      onClick={(e) => { e.stopPropagation(); removeProduct(p.ean, p.artikelnummer); }}
                      disabled={saving}
                      data-testid={`remove-product-${p.ean}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Add product row */}
          <div className="border-t border-border p-3">
            {showAddProduct ? (
              <div className="flex items-center gap-2">
                <Input
                  value={addEan}
                  onChange={(e) => setAddEan(e.target.value)}
                  placeholder="EAN / streckkod"
                  className="flex-1 h-8 text-sm font-mono"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") addProduct(); if (e.key === "Escape") setShowAddProduct(false); }}
                  data-testid="add-product-ean"
                />
                <Input
                  type="number"
                  min="1"
                  value={addQty}
                  onChange={(e) => setAddQty(e.target.value)}
                  className="w-20 h-8 text-sm text-right"
                  placeholder="Antal"
                  data-testid="add-product-qty"
                />
                <Button size="sm" className="h-8" onClick={addProduct} disabled={saving || !addEan.trim()} data-testid="add-product-confirm">
                  <Plus className="h-4 w-4 mr-1" /> Lagg till
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setShowAddProduct(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setShowAddProduct(true)} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-1" /> Lagg till produkt
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card className="p-8 text-center" data-testid="no-products-found">
          <Package className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground mb-4">Inga utlevererade produkter hittades for referens {selectedRef}</p>
          {showAddProduct ? (
            <div className="flex items-center gap-2 justify-center max-w-lg mx-auto">
              <Input
                value={addEan}
                onChange={(e) => setAddEan(e.target.value)}
                placeholder="EAN / streckkod"
                className="flex-1 h-8 text-sm font-mono"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") addProduct(); if (e.key === "Escape") setShowAddProduct(false); }}
                data-testid="add-product-ean"
              />
              <Input
                type="number"
                min="1"
                value={addQty}
                onChange={(e) => setAddQty(e.target.value)}
                className="w-20 h-8 text-sm text-right"
                placeholder="Antal"
                data-testid="add-product-qty"
              />
              <Button size="sm" className="h-8" onClick={addProduct} disabled={saving || !addEan.trim()} data-testid="add-product-confirm">
                <Plus className="h-4 w-4 mr-1" /> Lagg till
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => setShowAddProduct(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setShowAddProduct(true)} data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-1" /> Lagg till produkt
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}


function SummaryBadge({ label, value, sub, icon: Icon, color, bg, trend, trendSuffix = "%", testId }) {
  const hasTrend = trend !== null && trend !== undefined;
  const trendUp = trend > 0;
  const trendDown = trend < 0;
  const trendColor = trendUp ? "text-green-400" : trendDown ? "text-red-400" : "text-muted-foreground";

  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-start gap-2.5">
          <div className={`p-2 rounded-lg ${bg} shrink-0`}>
            <Icon className={`h-4 w-4 ${color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground truncate">{label}</p>
            <div className="flex items-center gap-1.5">
              <p className={`text-lg font-bold leading-tight ${color}`}>{value}</p>
              {hasTrend && (
                <span className={`inline-flex items-center text-xs font-semibold ${trendColor}`} data-testid={`${testId}-trend`}>
                  {trendUp ? (
                    <TrendingUp className="h-3 w-3 mr-0.5" />
                  ) : trendDown ? (
                    <TrendingDown className="h-3 w-3 mr-0.5" />
                  ) : null}
                  {trendUp ? "+" : ""}{trend}{trendSuffix}
                </span>
              )}
            </div>
            {sub && <p className="text-xs text-muted-foreground truncate mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function SortableHead({ label, sortKey, currentKey, dir, onClick, className = "" }) {
  const isActive = currentKey === sortKey;
  return (
    <TableHead
      className={`${className} cursor-pointer select-none hover:text-foreground transition-colors group`}
      onClick={() => onClick(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          dir === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-primary" /> : <ArrowDown className="h-3.5 w-3.5 text-primary" />
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-0 group-hover:opacity-40 transition-opacity" />
        )}
      </span>
    </TableHead>
  );
}
